import moment from "moment-timezone";
import { Storage } from "aws-amplify";

import { moment as momentConst } from "../constants";
import { getPropertyEventsForInstance } from "./appsyncOperations";
import config from "../config";

const reader = (file) => {
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onload = () => resolve(fileReader.result);
    fileReader.readAsDataURL(file);
  });
};

const addCoverPage = async ({ endDate, serviceProvider, startDate }) => {
  const content = [];
  const toc = {
    toc: {
      id: "mainToc",
      title: {
        text: "Report Overview",
        style: "header",
        margin: [0, 64, 0, 0],
      },
    },
  };
  content.push({
    width: "*",
    style: "title",
    alignment: "center",
    text: serviceProvider.fullName,
  });
  content.push({
    alignment: "center",
    style: "dateRange",
    text: `${moment(startDate).format(momentConst.format)}to ${moment(
      endDate
    ).format(momentConst.format)}`,
    width: "*",
  });
  try {
    const url = await Storage.get(serviceProvider.logoPath, {
      expires: 60,
      bucket: config.s3.spBucket,
    });
    const response = await fetch(url);
    if (response.ok) {
      const blob = await response.blob();
      content.push({
        image: await reader(blob),
        width: 150,
        alignment: "center",
        style: "picture",
      });
    }
  } catch (e) {
    console.log(e);
  }
  return { content, toc };
};

const getValue = (input, callback = (input) => input) =>
  input ? callback(input) : "N/A";

const addAttachedMedia = async (attachedMedia, timezone) => {
  const content = [];
  if (!(attachedMedia && attachedMedia.length > 0)) {
    return content;
  }
  for (const media of attachedMedia) {
    const url = await Storage.get(media.mediaId, {
      expires: 60,
    });
    const response = await fetch(url);
    const blob = await response.blob();
    content.push({
      alignment: "center",
      image: await reader(blob),
      style: "picture",
      width: 450,
    });
    if (media.createdAt) {
      content.push({
        style: "pictureTimestamp",
        text: moment(media.createdAt).tz(timezone).format(momentConst.format),
      });
    }
    if (media.description) {
      content.push({
        style: "pictureDescription",
        text: media.description,
      });
    }
  }
  return content;
};

const addDars = async (dars, properties, timeFormat) => {
  const content = [];
  let toc = null;
  if (dars && dars.length > 0) {
    toc = {
      toc: {
        id: "darToc",
        title: {
          style: "tocHeadingOne",
          text: "Daily Activity Reports",
        },
      },
    };
    const darsByPropertyId = dars.reduce((acc, dar) => {
      const existingDars = acc[dar.propertyId] ? acc[dar.propertyId] : [];
      return {
        ...acc,
        [dar.propertyId]: [...existingDars, dar],
      };
    }, {});
    for (const propertyId of Object.keys(darsByPropertyId)) {
      const property = properties[propertyId];
      const dars = Object.values(darsByPropertyId[propertyId]);
      for (const dar of dars) {
        // Clean single-line format without boxes
        const startTime = moment(dar.startTime).tz(property.place.timezone).format(timeFormat);
        const endTime = moment(dar.endTime).tz(property.place.timezone).format(timeFormat);
        const vipStatus = dar.isVacationWatch ? dar.isVacationWatch : (dar.isCardinal ? dar.isCardinal : '');

        // Create a report entry with notes on separate line
        const vipText = vipStatus ? ` | VIP: ${vipStatus}` : '';
        const reportLine = `${startTime} - ${endTime} | Officer: ${dar.displayName} | ${property.displayName}${vipText}`;
        // Add property header for first report of each property (no page break)
        if (dars.indexOf(dar) === 0) {
          const header = {
            style: "header",
            text: property.displayName,
            tocItem: "darToc",
            tocMargin: [16, 0, 4, 8],
            margin: [0, 8, 0, 4],
          };
          content.push(header);
        }

        // Add the report line
        content.push({
          text: [ { text: "DAILY ACTIVITY\n", bold: true}, reportLine],
          style: "reportLine",
        });

        // Add notes on separate line with indentation
        content.push({
          text: [ { text: "Notes: "}, `${dar.notes || 'N/A'}`],
          style: "messageLine",
          margin: [0, 0, 0, 8], // Left margin for indentation
        });
        if (dar.attachedMedia && dar.attachedMedia.length > 0) {
          const media = await addAttachedMedia(
            dar.attachedMedia,
            property.place.timezone
          );
          content.push(...media);
        }
      }
    }
  }
  return { content, toc };
};

const addResponseReports = async (
  responseIds,
  isAttachedResponse,
  properties,
  timeFormat
) => {
  const content = [];
  let toc = null;
  if (responseIds && responseIds.length > 0) {
    if (!isAttachedResponse) {
      toc = {
        toc: {
          id: "responseToc",
          title: {
            text: "Responses",
            style: "tocHeadingOne",
          },
        },
      };
    }
    const responsesByPropertyId = {};
    for (const responseId of responseIds) {
      const events = await getPropertyEventsForInstance(responseId);
      const propertyId = events[0].propertyId;
      const existingResponses = responsesByPropertyId[propertyId]
        ? responsesByPropertyId[propertyId]
        : [];
      responsesByPropertyId[propertyId] = [...existingResponses, events];
    }
    for (const propertyId of Object.keys(responsesByPropertyId)) {
      if (isAttachedResponse) {
        content.push({ text: "Attached Response", style: "subheader" });
      }
      const property = properties[propertyId];
      const responses = Object.values(responsesByPropertyId[propertyId]);
      for (const response of responses) {
        if (!isAttachedResponse) {
          // Add property header for first response of each property (no page break for compactness)
          if (responses.indexOf(response) === 0) {
            const header = {
              style: "header",
              text: property.displayName,
              tocItem: "responseToc",
              tocMargin: [16, 0, 4, 8],
              margin: [0, 8, 0, 4],
            };
            content.push(header);
          }
        }
        // Compact table format: combine property info and messages in one table
        const rows = [
          [
            { 
              style: "tableHeader", 
              text: "RESPONSE",
              colSpan: 3,
              fillColor: "#000000",
              fillOpacity: 0.1,
            },
            {},
            {},
          ],
          [
            { 
              style: "tableItem", 
              text: property.displayName,
              colSpan: 3,
              fontSize: 10,
            },
            {},
            {},
          ],
          [
            { 
              style: "tableItem", 
              text: property.place.formattedAddress,
              colSpan: 3,
              fontSize: 9,
              color: "#666666",
            },
            {},
            {},
          ],
          [
            { 
              style: "tableHeader", 
              text: "Time", 
              fontSize: 10,
              margin: [2, 4],
            },
            { 
              style: "tableHeader", 
              text: "Name", 
              fontSize: 10,
              margin: [2, 4],
            },
            { 
              style: "tableHeader", 
              text: "Message", 
              fontSize: 10,
              margin: [2, 4],
            },
          ],
        ];
        for (const event of response.reverse()) {
          rows.push([
            {
              style: "tableItem",
              text: moment(event.createdAt)
                .tz(property.place.timezone)
                .format(timeFormat),
              fontSize: 9,
              margin: [2, 4],
              noWrap: true,
            },
            { 
              style: "tableItem", 
              text: event.senderName, 
              fontSize: 9,
              margin: [2, 4],
            },
            { 
              style: "tableItem", 
              text: event.text, 
              fontSize: 9,
              margin: [2, 4],
            },
          ]);
        }
        content.push({
          fillColor: "#000000",
          fillOpacity: 0.05,
          layout: "lightHorizontalLines",
          style: "table",
          table: {
            body: rows,
            headerRows: 3,
            widths: [140, 60, "*"],
          },
          margin: [0, 0, 0, 8],
        });
      }
    }
  }
  return { content, toc };
};

const addIncidentReports = async (incidents, properties, timeFormat) => {
  const content = [];
  let toc = null;
  if (incidents && incidents.length > 0) {
    toc = {
      toc: {
        id: "incidentToc",
        title: {
          style: "tocHeadingOne",
          text: "Incident Reports",
        },
      },
    };
    const incidentsByPropertyId = incidents.reduce((acc, incident) => {
      const existingIncidents = acc[incident.propertyId]
        ? acc[incident.propertyId]
        : [];
      return {
        ...acc,
        [incident.propertyId]: [...existingIncidents, incident],
      };
    }, {});
    for (const propertyId of Object.keys(incidentsByPropertyId)) {
      const property = properties[propertyId];
      const incidentReports = Object.values(incidentsByPropertyId[propertyId]);
      for (const incidentReport of incidentReports) {
        // Add property header for first incident of each property (no page break)
        if (incidentReports.indexOf(incidentReport) === 0) {
          const header = {
            style: "header",
            text: property.displayName,
            tocItem: "incidentToc",
            tocMargin: [16, 0, 4, 8],
            margin: [0, 8, 0, 4],
          };
          content.push(header);
        }
        // Clean single-line format for incidents
        const startTime = getValue(incidentReport.startTime, (time) =>
          moment(time).tz(property.place.timezone).format(timeFormat)
        );
        const endTime = getValue(incidentReport.endTime, (time) =>
          moment(time).tz(property.place.timezone).format(timeFormat)
        );
        const submittedAt = moment(incidentReport.submitted.at)
          .tz(property.place.timezone)
          .format(timeFormat);

        // Create an incident entry with description on separate line
        const incidentLine = `${startTime} - ${endTime} | ${getValue(incidentReport.title)} | Officer: ${incidentReport.submitted.by} | Submitted: ${submittedAt}`;
        content.push({
          text: [ { text: "INCIDENT | ", bold: true }, incidentLine],
          style: "reportLine",
        });

        // Add description on separate line with proper indentation
        content.push({
          text: `- Description: ${getValue(incidentReport.description)}`,
          style: "messageLine",
          margin: [20, 0, 0, 0], // Left margin for indentation
        });
        if (incidentReport.connectedInstance) {
          const responses = await addResponseReports(
            [incidentReport.connectedInstance],
            true,
            properties,
            property.place.timezone,
            timeFormat
          );
          content.push(...responses.content);
        }
        if (incidentReport.policeCase) {
          content.push({ style: "subheaderTwo", text: "Police Case" });
          content.push({
            fillColor: "#000000",
            fillOpacity: 0.08,
            layout: "lightHorizontalLines",
            style: "table",
            table: {
              body: [
                [{ style: "tableHeader", text: "Police Case Information" }, ""],
                [
                  { style: "tableItem", text: "Agency" },
                  {
                    style: "tableItem",
                    text: incidentReport.policeCase.agency,
                  },
                ],
                [
                  { style: "tableItem", text: "Case Number" },
                  {
                    style: "tableItem",
                    text: incidentReport.policeCase.number,
                  },
                ],
              ],
              headerRows: 1,
              widths: ["*", "*"],
            },
          });
        }
        if (incidentReport.subjects) {
          content.push({ style: "subheaderTwo", text: "Subjects" });
          for (const subject of incidentReport.subjects) {
            content.push({
              fillColor: "#000000",
              fillOpacity: 0.08,
              layout: "lightHorizontalLines",
              style: "table",
              table: {
                body: [
                  [{ style: "tableHeader", text: "Subject Information" }, ""],
                  [
                    { style: "tableItem", text: "Name" },
                    { style: "tableItem", text: subject.name },
                  ],
                  [
                    { style: "tableItem", text: "Type" },
                    { style: "tableItem", text: getValue(subject.type) },
                  ],
                  [
                    { style: "tableItem", text: "Sex" },
                    { style: "tableItem", text: getValue(subject.sex) },
                  ],
                  [
                    { style: "tableItem", text: "Ethnicity" },
                    { style: "tableItem", text: getValue(subject.ethnicity) },
                  ],
                  [
                    { style: "tableItem", text: "Race" },
                    { style: "tableItem", text: getValue(subject.race) },
                  ],
                  [
                    { style: "tableItem", text: "Phone Number" },
                    { style: "tableItem", text: getValue(subject.phoneNumber) },
                  ],
                  [
                    { style: "tableItem", text: "Address" },
                    { style: "tableItem", text: getValue(subject.address) },
                  ],
                  [
                    { style: "tableItem", text: "Description" },
                    { style: "tableItem", text: getValue(subject.description) },
                  ],
                  [
                    { style: "tableItem", text: "Height" },
                    { style: "tableItem", text: getValue(subject.height) },
                  ],
                  [
                    { style: "tableItem", text: "Weight" },
                    { style: "tableItem", text: getValue(subject.weight) },
                  ],
                  [
                    { style: "tableItem", text: "Date Of Birth" },
                    { style: "tableItem", text: getValue(subject.dateOfBirth) },
                  ],
                  [
                    { style: "tableItem", text: "Age" },
                    { style: "tableItem", text: getValue(subject.age) },
                  ],
                  [
                    { style: "tableItem", text: "Hair Color" },
                    { style: "tableItem", text: getValue(subject.hairColor) },
                  ],
                  [
                    { style: "tableItem", text: "Eye Color" },
                    { style: "tableItem", text: getValue(subject.eyeColor) },
                  ],
                  [
                    { style: "tableItem", text: "License" },
                    {
                      style: "tableItem",
                      text: getValue(
                        subject.license,
                        (license) =>
                          `State: ${license.state}, Number: ${license.number}`
                      ),
                    },
                  ],
                ],
                headerRows: 1,
                widths: ["*", "*"],
              },
            });
          }
        }
        if (incidentReport.vehicles) {
          content.push({ style: "subheaderTwo", text: "Vehicles" });
          for (const vehicle of incidentReport.vehicles) {
            content.push({
              fillColor: "#000000",
              fillOpacity: 0.08,
              layout: "lightHorizontalLines",
              style: "table",
              table: {
                body: [
                  [{ style: "tableHeader", text: "Vehicle Information" }, ""],
                  [
                    { style: "tableItem", text: "Type" },
                    { style: "tableItem", text: getValue(vehicle.type) },
                  ],
                  [
                    { style: "tableItem", text: "Description" },
                    { style: "tableItem", text: getValue(vehicle.description) },
                  ],
                  [
                    { style: "tableItem", text: "Make" },
                    { style: "tableItem", text: getValue(vehicle.make) },
                  ],
                  [
                    { style: "tableItem", text: "Model" },
                    { style: "tableItem", text: getValue(vehicle.model) },
                  ],
                  [
                    { style: "tableItem", text: "Year" },
                    { style: "tableItem", text: getValue(vehicle.year) },
                  ],
                  [
                    { style: "tableItem", text: "Color" },
                    { style: "tableItem", text: getValue(vehicle.color) },
                  ],
                  [
                    { style: "tableItem", text: "Vin" },
                    { style: "tableItem", text: getValue(vehicle.vin) },
                  ],
                  [
                    { style: "tableItem", text: "Registered Owner" },
                    {
                      style: "tableItem",
                      text: getValue(vehicle.registeredOwner),
                    },
                  ],
                  [
                    { style: "tableItem", text: "Status" },
                    { style: "tableItem", text: getValue(vehicle.status) },
                  ],
                  [
                    { style: "tableItem", text: "License" },
                    {
                      style: "tableItem",
                      text: getValue(
                        vehicle.vehicleLicense,
                        (license) =>
                          `State: ${license.state}, Number: ${license.number}`
                      ),
                    },
                  ],
                ],
                headerRows: 1,
                widths: ["*", "*"],
              },
            });
          }
        }
        if (incidentReport.attachedMedia) {
          const media = await addAttachedMedia(
            incidentReport.attachedMedia,
            property.place.timezone
          );
          content.push(...media);
        }
      }
    }
  }
  return { content, toc };
};

export const createReportContent = async ({
  dars = null,
  endDate,
  incidents = null,
  responses = null,
  properties,
  serviceProvider,
  startDate,
  timeFormat,
}) => {
  const toc = [];
  const { content: coverPageContent, toc: coverPageToc } = await addCoverPage({
    endDate,
    serviceProvider,
    startDate,
  });
  toc.push(coverPageToc);
  const { content: darsContent, toc: darsToc } = await addDars(
    dars,
    properties,
    timeFormat
  );
  if (darsToc) {
    toc.push(darsToc);
  }
  const {
    content: incidentsContent,
    toc: incidentsToc,
  } = await addIncidentReports(incidents, properties, timeFormat);
  if (incidentsToc) {
    toc.push(incidentsToc);
  }
  const {
    content: responsesContent,
    toc: responsesToc,
  } = await addResponseReports(
    responses && responses.length > 0
      ? responses.map((response) => response.instanceId)
      : [],
    false,
    properties,
    timeFormat
  );
  if (responsesToc) {
    toc.push(responsesToc);
  }
  return [
    ...coverPageContent,
    ...toc,
    ...darsContent,
    ...incidentsContent,
    ...responsesContent,
  ];
};
